import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from '../components/Helmet';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Target, Users, BarChart2, Lightbulb, Rocket,
  UserCheck, Building2, Briefcase, UsersRound,
  Hash, CheckCircle, ArrowRight, Send, ChevronLeft
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

type Option = { value: string; label: string; icon?: React.ReactNode; desc?: string };

type Step = {
  id: string;
  question: string;
  subtitle?: string;
  options: Option[];
};

type Answers = Record<string, string>;

type Formation = {
  title: string;
  desc: string;
  duration: string;
  public: string;
  slug: string;
  why: string;
};

// ─── Données quiz ──────────────────────────────────────────────────────────────

const steps: Step[] = [
  {
    id: 'objectif',
    question: 'Quel est votre objectif principal ?',
    subtitle: 'Choisissez la priorité qui correspond le mieux à votre situation actuelle.',
    options: [
      { value: 'sensibiliser', label: 'Sensibiliser mes équipes à l\'IA', icon: <Lightbulb className="h-6 w-6" />, desc: 'Créer une culture IA commune, réduire les peurs' },
      { value: 'federer', label: 'Fédérer autour d\'un projet IA', icon: <UsersRound className="h-6 w-6" />, desc: 'Cohésion d\'équipe, engagement collectif' },
      { value: 'former', label: 'Former en profondeur mes équipes', icon: <BarChart2 className="h-6 w-6" />, desc: 'Montée en compétences opérationnelles' },
      { value: 'identifier', label: 'Identifier nos cas d\'usage IA', icon: <Target className="h-6 w-6" />, desc: 'Trouver les gains concrets pour nos métiers' },
      { value: 'transformer', label: 'Transformer notre organisation', icon: <Rocket className="h-6 w-6" />, desc: 'Déployer l\'IA comme levier stratégique durable' },
    ],
  },
  {
    id: 'public',
    question: 'Quel est votre public cible ?',
    subtitle: 'Qui participera à la formation ou à l\'atelier ?',
    options: [
      { value: 'tous', label: 'Tous les collaborateurs', icon: <UsersRound className="h-6 w-6" />, desc: 'Niveaux et métiers mixtes' },
      { value: 'managers', label: 'Managers et encadrants', icon: <UserCheck className="h-6 w-6" />, desc: 'Responsables d\'équipe, chefs de projet' },
      { value: 'direction', label: 'Dirigeants et CODIR', icon: <Briefcase className="h-6 w-6" />, desc: 'Décideurs stratégiques' },
      { value: 'metier', label: 'Équipes métier spécifiques', icon: <Building2 className="h-6 w-6" />, desc: 'RH, marketing, commercial, juridique...' },
    ],
  },
  {
    id: 'participants',
    question: 'Combien de participants attendez-vous ?',
    options: [
      { value: 'moins10', label: 'Moins de 10', icon: <Hash className="h-6 w-6" />, desc: 'Format atelier intensif' },
      { value: '10a20', label: '10 à 20', icon: <Hash className="h-6 w-6" />, desc: 'Format atelier ou formation' },
      { value: '20a50', label: '20 à 50', icon: <Hash className="h-6 w-6" />, desc: 'Format séminaire ou team building' },
      { value: 'plus50', label: 'Plus de 50', icon: <Hash className="h-6 w-6" />, desc: 'Grand format' },
    ],
  },
  {
    id: 'maturite',
    question: 'Quelle est votre maturité IA actuelle ?',
    subtitle: 'Évaluez honnêtement le niveau moyen de vos équipes.',
    options: [
      { value: 'debutant', label: 'Débutants', icon: <BarChart2 className="h-6 w-6" />, desc: 'Peu ou pas d\'expérience avec les outils IA' },
      { value: 'intermediaire', label: 'Intermédiaires', icon: <BarChart2 className="h-6 w-6" />, desc: 'Quelques outils testés, usages ponctuels' },
      { value: 'avance', label: 'Avancés', icon: <BarChart2 className="h-6 w-6" />, desc: 'Outils IA déjà intégrés dans le quotidien' },
    ],
  },
  {
    id: 'delai',
    question: 'Quel est votre délai souhaité ?',
    options: [
      { value: 'urgent', label: 'Dans le mois', desc: 'Besoin urgent ou événement planifié' },
      { value: 'court', label: 'Dans les 3 mois', desc: 'Projet en cours de planification' },
      { value: 'flexible', label: 'Flexible', desc: 'Pas de contrainte de date particulière' },
    ],
  },
];

// ─── Logique de recommandation ─────────────────────────────────────────────────

const formations: Record<string, Formation> = {
  webinaire: {
    title: 'Webinaire de Sensibilisation IA',
    desc: 'Une session courte et impactante pour démystifier l\'IA auprès de l\'ensemble de vos collaborateurs. Accessible à tous, sans prérequis.',
    duration: '2 heures',
    public: 'Tout public',
    slug: '/DemandeInfo',
    why: 'Votre priorité est la sensibilisation rapide d\'un groupe. Le webinaire est le format le plus accessible et le plus rapide à organiser.',
  },
  seminaire: {
    title: 'Séminaire Grand Format',
    desc: 'Un séminaire inspirant pour embarquer une large audience dans la transformation IA. Idéal pour les organisations de plus de 50 personnes.',
    duration: '1/2 journée',
    public: 'Large audience (10–200 pers.)',
    slug: '/DemandeInfo',
    why: 'Votre groupe important nécessite un format conçu pour les grandes audiences, avec animation en plénière et ateliers en sous-groupes.',
  },
  teambuilding: {
    title: 'Team Building IA',
    desc: 'Découvrir l\'IA en équipe, de manière ludique et collaborative. Le format idéal pour créer un élan collectif autour de l\'IA.',
    duration: '1 journée',
    public: 'Équipes (10–30 pers.)',
    slug: '/DemandeInfo',
    why: 'Votre objectif de cohésion et d\'engagement collectif correspond parfaitement au format team building IA.',
  },
  etatart: {
    title: 'Formation État de l\'art de l\'IA',
    desc: 'Le socle complet pour comprendre les technologies IA, identifier les opportunités et acquérir un vocabulaire commun robuste.',
    duration: '2 journées',
    public: 'Tout public',
    slug: '/DemandeInfo',
    why: 'Votre niveau débutant ou intermédiaire et votre objectif de montée en compétences structurée correspondent à ce format complet.',
  },
  managers: {
    title: 'Formation IA : Comprendre, Décider, Prioriser',
    desc: 'Une journée dédiée aux managers pour structurer une vision IA, évaluer les opportunités et repartir avec une liste de cas d\'usage prioritaires.',
    duration: '1 journée',
    public: 'Managers & Dirigeants',
    slug: '/DemandeInfo',
    why: 'En tant que décideur, vous avez besoin d\'une formation orientée stratégie et arbitrage — pas d\'un tutoriel technique.',
  },
  atelier: {
    title: 'Atelier Métiers : Identifier vos Cas d\'Usage IA',
    desc: 'Un atelier collaboratif pour faire émerger les cas d\'usage IA concrets et prioritaires pour vos équipes métier spécifiques.',
    duration: '1 journée',
    public: 'Équipes métier (5–20 pers.)',
    slug: '/DemandeInfo',
    why: 'Vous souhaitez identifier des gains concrets par métier. L\'atelier co-construction est le format le plus direct pour y parvenir.',
  },
  parcours: {
    title: 'Parcours : De l\'Acculturation à l\'Action',
    desc: 'Notre accompagnement le plus complet. Nous co-construisons avec vous une feuille de route et lançons ensemble un premier pilote IA.',
    duration: '> 5 jours',
    public: 'Direction & CODIR',
    slug: '/DemandeInfo',
    why: 'Votre ambition de transformation structurelle nécessite un accompagnement sur la durée, au-delà d\'une simple formation.',
  },
};

function recommend(answers: Answers): Formation {
  const { objectif, public: pub, participants, maturite } = answers;

  if (objectif === 'transformer') return formations.parcours;
  if (objectif === 'federer') return formations.teambuilding;
  if (objectif === 'identifier') return formations.atelier;

  if (objectif === 'sensibiliser') {
    if (participants === 'plus50') return formations.seminaire;
    return formations.webinaire;
  }

  // objectif === 'former'
  if (pub === 'direction' || pub === 'managers') return formations.managers;
  if (pub === 'metier') return formations.atelier;
  if (maturite === 'avance') return formations.atelier;
  return formations.etatart;
}

// ─── Composant principal ───────────────────────────────────────────────────────

const DiagnosticPage = () => {
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [contactInfo, setContactInfo] = useState({ prenom: '', nom: '', email: '', entreprise: '' });
  const [showResult, setShowResult] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const defaultTitle = 'Quel format de formation IA pour votre entreprise ?';
  const urlTitle = searchParams.get('title');
  const pageTitle = urlTitle ? decodeURIComponent(urlTitle) : defaultTitle;

  const totalSteps = steps.length;
  const isContactStep = currentStep === totalSteps;
  const progress = isContactStep || showResult ? 100 : Math.round(((currentStep) / totalSteps) * 100);

  const handleSelect = (value: string) => {
    const newAnswers = { ...answers, [steps[currentStep].id]: value };
    setAnswers(newAnswers);
    setTimeout(() => {
      if (currentStep < totalSteps - 1) setCurrentStep(currentStep + 1);
      else setCurrentStep(totalSteps); // go to contact step
    }, 300);
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactInfo.email || !contactInfo.nom || !contactInfo.entreprise) return;

    setSending(true);
    const result = recommend(answers);
    const formData = new FormData();
    formData.append('access_key', '5bf31f55-ad2e-42d0-b75d-da315233314c');
    formData.append('subject', `[Diagnostic IA] ${contactInfo.prenom} ${contactInfo.nom} — ${contactInfo.entreprise}`);
    formData.append('name', `${contactInfo.prenom} ${contactInfo.nom}`);
    formData.append('email', contactInfo.email);
    formData.append('entreprise', contactInfo.entreprise);
    formData.append('formation_recommandee', result.title);
    formData.append('reponses_diagnostic', JSON.stringify(answers));
    formData.append('message',
      `Résultat du diagnostic IA en ligne\n\n` +
      `Prénom : ${contactInfo.prenom}\nNom : ${contactInfo.nom}\nEmail : ${contactInfo.email}\nEntreprise : ${contactInfo.entreprise}\n\n` +
      `Réponses :\n` +
      Object.entries(answers).map(([k, v]) => `- ${k}: ${v}`).join('\n') +
      `\n\nFormation recommandée : ${result.title}`
    );

    try {
      const response = await fetch('https://api.web3forms.com/submit', { method: 'POST', body: formData });
      const res = await response.json();
      if (res.success) {
        setSubmitted(true);
        setShowResult(true);
        if (typeof (window as any).gtag_report_conversion === 'function') {
          (window as any).gtag_report_conversion();
        }
      }
    } catch {
      // show result anyway
      setShowResult(true);
    } finally {
      setSending(false);
    }
  };

  const recommendation = showResult ? recommend(answers) : null;

  return (
    <>
      <Helmet>
        <title>{pageTitle} | Upnov IA</title>
        <meta name="description" content="5 questions pour identifier la formation IA la plus adaptée à votre entreprise. Résultat immédiat et personnalisé." />
        <link rel="canonical" href="https://www.upnov.com/demarrer-votre-diagnostic-ia-entreprise" />
        <meta property="og:title" content="Diagnostic IA : Trouvez la formation parfaite pour votre équipe" />
        <meta property="og:description" content="5 questions pour identifier la formation IA la plus adaptée à votre entreprise. Résultat immédiat et personnalisé." />
        <meta property="og:url" content="https://www.upnov.com/demarrer-votre-diagnostic-ia-entreprise" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.upnov.com/diagnosticIA.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <script>
          {`
            function gtag_report_conversion() {
              console.log('envoi conversion diagnostic')
              if (typeof gtag === 'function') {
                gtag('event', 'conversion', {
                  send_to: 'AW-998086090/e5KICN_0qOIbEMqr9tsD',
                  value: 1.0,
                  currency: 'EUR'
                });
                console.log('conversion envoyée')
              }
            }
          `}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="bg-primary text-white py-12">
        <div className="container-custom text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm mb-4">
            <Target className="h-4 w-4" /> Diagnostic personnalisé — 5 minutes
          </div>
          <h1 className="heading-md mb-3">{pageTitle}</h1>
          <p className="text-white/80">Découvrez comment l'IA peut faire gagner jusqu'à 30% de productivité à vos équipes.</p>
        </div>
      </section>

      {/* Progress bar */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="container-custom py-3 max-w-2xl mx-auto">
          <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
            <span>{showResult ? 'Votre résultat' : isContactStep ? 'Vos coordonnées' : `Question ${currentStep + 1} / ${totalSteps}`}</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              className="bg-primary h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
        </div>
      </div>

      <div className="section-padding bg-gray-50 min-h-screen">
        <div className="container-custom max-w-2xl mx-auto">
          <AnimatePresence mode="wait">

            {/* ── Steps ──────────────────────────────────────────── */}
            {!isContactStep && !showResult && (
              <motion.div
                key={`step-${currentStep}`}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.25 }}
              >
                <div className="bg-white rounded-2xl shadow-sm p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{steps[currentStep].question}</h2>
                  {steps[currentStep].subtitle && (
                    <p className="text-gray-500 mb-6">{steps[currentStep].subtitle}</p>
                  )}
                  <div className="grid gap-3">
                    {steps[currentStep].options.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => handleSelect(opt.value)}
                        className={`w-full flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all duration-150 hover:border-primary hover:bg-primary/5 ${
                          answers[steps[currentStep].id] === opt.value
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 bg-white'
                        }`}
                      >
                        {opt.icon && (
                          <span className={`mt-0.5 shrink-0 ${answers[steps[currentStep].id] === opt.value ? 'text-primary' : 'text-gray-400'}`}>
                            {opt.icon}
                          </span>
                        )}
                        <span>
                          <span className="block font-semibold text-gray-800">{opt.label}</span>
                          {opt.desc && <span className="text-sm text-gray-500">{opt.desc}</span>}
                        </span>
                      </button>
                    ))}
                  </div>
                  {currentStep > 0 && (
                    <button onClick={handleBack} className="mt-6 flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 transition-colors">
                      <ChevronLeft className="h-4 w-4" /> Question précédente
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {/* ── Contact step ────────────────────────────────────── */}
            {isContactStep && !showResult && (
              <motion.div
                key="contact"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.25 }}
              >
                <div className="bg-white rounded-2xl shadow-sm p-8">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-full mb-4">
                      <CheckCircle className="h-7 w-7 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Votre diagnostic est prêt !</h2>
                    <p className="text-gray-500">Entrez vos coordonnées pour recevoir votre recommandation personnalisée.</p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                        <input
                          type="text" required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          placeholder="Votre prénom"
                          value={contactInfo.prenom}
                          onChange={e => setContactInfo({ ...contactInfo, prenom: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                        <input
                          type="text" required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          placeholder="Votre nom"
                          value={contactInfo.nom}
                          onChange={e => setContactInfo({ ...contactInfo, nom: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email professionnel *</label>
                      <input
                        type="email" required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                        placeholder="prenom@entreprise.com"
                        value={contactInfo.email}
                        onChange={e => setContactInfo({ ...contactInfo, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise *</label>
                      <input
                        type="text" required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                        placeholder="Nom de votre entreprise"
                        value={contactInfo.entreprise}
                        onChange={e => setContactInfo({ ...contactInfo, entreprise: e.target.value })}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={sending}
                      className="w-full btn-primary py-4 flex items-center justify-center gap-2 mt-2"
                    >
                      {sending ? 'Analyse en cours...' : (
                        <><Send className="h-5 w-5" /> Voir ma recommandation</>
                      )}
                    </button>

                    <p className="text-xs text-gray-400 text-center">
                      Vos données sont utilisées uniquement pour vous envoyer votre recommandation. Aucun spam.
                    </p>
                  </form>

                  <button onClick={handleBack} className="mt-4 flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 transition-colors">
                    <ChevronLeft className="h-4 w-4" /> Modifier mes réponses
                  </button>
                </div>
              </motion.div>
            )}

            {/* ── Result ──────────────────────────────────────────── */}
            {showResult && recommendation && (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35 }}
              >
                {submitted && (
                  <div className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 rounded-xl px-4 py-3 mb-4 text-sm">
                    <CheckCircle className="h-5 w-5 shrink-0" />
                    Votre recommandation a été envoyée à {contactInfo.email}
                  </div>
                )}

                <div className="bg-white rounded-2xl shadow-sm p-8">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-4">
                      <Rocket className="h-7 w-7 text-green-600" />
                    </div>
                    <p className="text-sm font-medium text-primary uppercase tracking-wider mb-1">Notre recommandation</p>
                    <h2 className="text-2xl font-bold text-gray-900">{recommendation.title}</h2>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-5 mb-6">
                    <p className="text-gray-700 mb-4">{recommendation.desc}</p>
                    <div className="flex flex-wrap gap-3 text-sm">
                      <span className="bg-white border border-gray-200 px-3 py-1 rounded-full text-gray-600">
                        ⏱ {recommendation.duration}
                      </span>
                      <span className="bg-white border border-gray-200 px-3 py-1 rounded-full text-gray-600">
                        👥 {recommendation.public}
                      </span>
                    </div>
                  </div>

                  <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-6">
                    <p className="text-sm font-medium text-primary mb-1">Pourquoi ce format pour vous ?</p>
                    <p className="text-sm text-gray-600">{recommendation.why}</p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link to={recommendation.slug} className="btn-primary flex-1 text-center flex items-center justify-center gap-2">
                      <ArrowRight className="h-5 w-5" /> Demander un devis gratuit
                    </Link>
                    <Link to="/formation-ia-entreprise/" className="btn-outline flex-1 text-center">
                      Voir toutes nos formations
                    </Link>
                  </div>
                </div>

                <div className="mt-6 bg-white rounded-2xl shadow-sm p-6 text-center">
                  <p className="text-gray-600 mb-3">Vous souhaitez affiner ce diagnostic avec un expert ?</p>
                  <Link to="/contact/" className="text-primary font-medium hover:underline flex items-center justify-center gap-1">
                    Parler à Cyril Cohen Solal <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>

                <div className="mt-4 text-center">
                  <button
                    onClick={() => { setAnswers({}); setContactInfo({ prenom: '', nom: '', email: '', entreprise: '' }); setCurrentStep(0); setShowResult(false); setSubmitted(false); }}
                    className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    Recommencer le diagnostic
                  </button>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </>
  );
};

export default DiagnosticPage;
